/* eslint-disable new-cap */
/* eslint-disable no-new */
import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import { GoogleApiWrapper } from 'google-maps-react';
import styled from 'styled-components';
import { LoaderDefault } from '@vr/lib';
import theme from '@vr/lib/styles/theme';
import { GOOGLE_MAPS_KEY } from '../config/constants';
import Popup from './Popup';
import MapaContext from '../context/Mapa';

import marcadorImage from '../static/marker.svg';

const MapContainer = styled.div`
  height: ${({ height }) => height};
  background: ${({ theme: { colors } }) => colors.white};

  .gm-style .gm-style-iw-d,
  .gm-style .gm-style-iw-c,
  .gm-style .gm-style-iw {
    overflow: initial !important;
  }

  button.gm-ui-hover-effect {
    border-radius: 15px;
    opacity: 1;
    width: 15px;
    height: 15px;
    top: 0px !important;
    right: 0px !important;
    overflow: hidden;

    img {
      display: none !important;
    }

    &:hover {
      > span {
        background-color: ${({ theme: { colors } }) => colors.institucional};
      }
    }
  }
`;

const mapOptions = {
  rotateControl: true,
  zoomControl: true,
  mapTypeControl: true,
  scaleControl: true,
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeID: 'roadmap',
  disableDefaultUI: true,
  mapTypeControlOptions: {
    mapTypeIds: [],
  },
};

function GoogleMaps({ google, center, zoom, initMapObj, config, height }) {
  const mapRef = useRef();
  const [mapObj, setMapObj] = useState(initMapObj);
  const [mapCircle, setMapCircle] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindow, setInfoWindow] = useState(null);
  const {
    locations,
    raio,
    empresaSelecionada,
    setEmpresaSelecionada,
    companyName,
  } = useContext(MapaContext);

  function initMap() {
    if (!mapRef.current || !google) {
      setTimeout(initMap, 100);
      return;
    }
    const map = new google.maps.Map(mapRef.current, {
      center,
      zoom,
      ...mapOptions,
      ...config,
    });
    setMapObj(map);
    setInfoWindow(
      new google.maps.InfoWindow({
        pixelOffset: new google.maps.Size(0, 10),
      }),
    );
  }

  function clearMarcadores() {
    if (markers.length > 0) {
      markers.forEach(item => item.marker.setMap(null));
      setMarkers([]);
    }
  }

  function setCenter() {
    if (!mapObj) {
      return;
    }
    clearMarcadores();
    mapObj.setCenter(center);
  }

  function setZoom() {
    if (!mapObj) {
      return;
    }
    mapObj.setZoom(zoom);
  }

  function renderInfoWindow(item) {
    return ReactDOMServer.renderToString(
      <Popup
        estabelecimento={item}
        center={center}
        companyName={companyName}
      />,
    );
  }

  function onClickMarcador(target, item) {
    infoWindow.open(mapObj, target);
    infoWindow.setContent(renderInfoWindow(item));
    setEmpresaSelecionada({ ...item, origem: 'mapa' });
  }

  function setLocations() {
    clearMarcadores();

    const listMarkers = [];

    locations.forEach(item => {
      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(item.latitude, item.longitude),
        map: mapObj,
        icon: {
          url: marcadorImage,
        },
      });

      marker.addListener('click', () => onClickMarcador(marker, item));
      listMarkers.push({ id: item.id, marker });
    });

    setMarkers(listMarkers);
  }

  function setRaio() {
    if (!mapObj) {
      return;
    }

    if (mapCircle) {
      mapCircle.setMap(null);
    }

    const circle = new google.maps.Circle({
      strokeColor: theme.colors.primary,
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillColor: theme.colors.primary,
      fillOpacity: 0.15,
      map: mapObj,
      radius: raio * 1000,
      clickable: false,
      center,
    });

    setMapCircle(circle);
  }

  function changeEmpresaSelecionada() {
    if (empresaSelecionada.origem !== 'mapa') {
      const [marcador] = markers.filter(
        item => item.id === empresaSelecionada.id,
      );

      if (marcador) {
        new google.maps.event.trigger(marcador.marker, 'click');
      }
    }
  }

  useEffect(initMap, []);
  useEffect(setCenter, [center]);
  useEffect(setZoom, [zoom]);
  useEffect(setLocations, [locations]);
  useEffect(setRaio, [raio, center]);
  useEffect(changeEmpresaSelecionada, [empresaSelecionada]);

  return <MapContainer height={height} id="vr-mapa" ref={mapRef} />;
}

GoogleMaps.propTypes = {
  google: PropTypes.shape({
    maps: PropTypes.object.isRequired,
  }).isRequired,
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  zoom: PropTypes.number,
  config: PropTypes.shape({}),
  height: PropTypes.string,
  initMapObj: PropTypes.shape({}), // Propriedade utilizada apenas para testes
};

GoogleMaps.defaultProps = {
  zoom: 4,
  center: { lat: -13.33, lng: -53.2 },
  config: {},
  height: '100vh',
  initMapObj: null,
};

export default GoogleApiWrapper({
  apiKey: GOOGLE_MAPS_KEY,
  language: 'pt-BR',
  LoadingContainer: LoaderDefault,
})(GoogleMaps);

export { GoogleMaps as GoogleMapsWithoutScript };
